import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  FileText,
  ReceiptEuro,
  Sparkles,
  Users,
} from "lucide-react";
import api, { dateTimeIt, eur } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import TimeSaved from "@/components/TimeSaved";

const Stat = ({ label, value, hint, testId }) => (
  <div className="card-flat p-6 transition-shadow duration-200 hover:shadow-sm" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="font-mono-num mt-2 text-2xl font-semibold text-slate-900">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const QUICK = [
  { to: "/documenti/nuovo?type=quote", labelKey: "dash.newQuote", icon: FileText, id: "quick-quote" },
  { to: "/documenti/nuovo?type=invoice", labelKey: "dash.newInvoice", icon: ReceiptEuro, id: "quick-invoice" },
  { to: "/clienti", labelKey: "dash.addClient", icon: Users, id: "quick-client" },
  { to: "/agenda", labelKey: "dash.planDay", icon: CalendarClock, id: "quick-agenda" },
];

const Dashboard = () => {
  const [data, setData] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {data.quotes_count === 0 && data.invoices_count === 0 && (
        <Link
          to="/benvenuto"
          data-testid="first-quote-banner"
          className="card-flat grain relative flex flex-wrap items-center justify-between gap-4 overflow-hidden bg-slate-950 px-6 py-6 text-white transition-transform duration-200 hover:-translate-y-0.5"
        >
          <div className="relative max-w-xl">
            <p className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Sparkles className="h-4 w-4 text-indigo-300" /> {t("dash.firstTitle")}
            </p>
            <p className="mt-2 text-sm text-slate-400">{t("dash.firstText")}</p>
          </div>
          <span className="relative inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold">
            {t("dash.firstCta")} <ArrowUpRight className="h-4 w-4" />
          </span>
        </Link>
      )}
      {data.overdue_count > 0 && (
        <Link
          to="/solleciti"
          data-testid="overdue-banner"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 transition-colors duration-200 hover:bg-amber-100"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            {data.overdue_count}{" "}
            {data.overdue_count === 1 ? t("dash.overdueOne") : t("dash.overdueMany")} ·{" "}
            <span className="font-mono-num">{eur(data.overdue_amount)}</span> {t("dash.toRecover")}
          </span>
          <span className="text-xs font-semibold text-amber-800 underline">
            {t("dash.generateReminders")}
          </span>
        </Link>
      )}
      <div className="grid gap-4 lg:grid-cols-4">
        <div className="card-flat relative overflow-hidden bg-slate-950 p-8 text-white lg:col-span-2 grain">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            {t("dash.revenue")}
          </p>
          <p className="font-mono-num mt-3 text-4xl font-semibold lg:text-5xl" data-testid="stat-revenue">
            {eur(data.revenue_total)}
          </p>
          <p className="mt-3 text-sm text-slate-400">
            {t("dash.pending")}:{" "}
            <span className="font-mono-num text-amber-300">{eur(data.revenue_pending)}</span>
          </p>
          {data.currencies?.length > 1 && (
            <p className="mt-2 text-xs text-slate-500" data-testid="multi-currency-note">
              {t("dash.multiCurrency")} {data.currencies.join(", ")}
            </p>
          )}
        </div>
        <Stat
          label={t("dash.openQuotes")}
          value={data.quotes_open}
          hint={`${data.quotes_count} ${t("dash.totalSuffix")}`}
          testId="stat-quotes"
        />
        <Stat label={t("dash.clients")} value={data.clients_count} hint={t("dash.inRegistry")} testId="stat-clients" />
        <Stat
          label={t("dash.tasksToday")}
          value={`${data.tasks_done_today}/${data.tasks_today}`}
          hint={t("dash.completed")}
          testId="stat-tasks"
        />
        <Stat
          label={t("dash.invoicesIssued")}
          value={data.invoices_count}
          hint={t("dash.totalSuffix")}
          testId="stat-invoices"
        />
        <div className="card-flat p-6 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("dash.quickActions")}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {QUICK.map(({ to, labelKey, icon: Icon, id }) => (
              <Link
                key={id}
                to={to}
                data-testid={id}
                className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5 text-sm font-medium transition-colors duration-200 hover:bg-slate-900 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4" /> {t(labelKey)}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      <TimeSaved />

      <div className="card-flat p-6" data-testid="recent-activities">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold md:text-lg">{t("dash.recentMemory")}</h2>
          <Link to="/memoria" className="text-xs font-semibold text-blue-600 hover:underline">
            {t("dash.seeAll")}
          </Link>
        </div>
        <ul className="mt-4 space-y-3">
          {data.recent_activities.length === 0 && (
            <li className="text-sm text-slate-500">{t("dash.noActivity")}</li>
          )}
          {data.recent_activities.map((a, i) => (
            <li
              key={a.id}
              className="rise flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{a.title}</p>
                <p className="font-mono-num text-xs text-slate-500">{dateTimeIt(a.created_at)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Dashboard;
